import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { MonitorSmartphone } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Count, EmptyState, Page, PageHeader, TableCard, Toolbar } from '@/components/shell'
import { api, ApiError, type SessionRow } from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import { useCan, useMe } from '@/lib/session'
import { roleLabel } from '../dashboard'

export const Route = createFileRoute('/dashboard/sessions')({
  component: SessionsPage,
})

/**
 * 在线会话。
 *
 * 这是迁到 Better Auth 之后才具备的能力——旧的 Payload 方案 `useSessions: false`，
 * JWT 无法单点吊销，既看不到谁在线，也没法把人踢下去，只能等 token 自然过期。
 */
function SessionsPage() {
  const can = useCan()
  const me = useMe()
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<{ data: SessionRow[] }>('/sessions'),
    // 会话状态变化快，比别处刷得勤一些
    refetchInterval: 30_000,
  })

  const revoke = useMutation({
    mutationFn: (userId: string) => api.post<{ revoked: number }>(`/sessions/revoke/${userId}`),
    onSuccess: (r) => {
      toast.success('已强制下线', { description: `吊销了 ${r.revoked} 个会话` })
      qc.invalidateQueries({ queryKey: ['sessions'] })
    },
    onError: (e) =>
      toast.error('操作失败', { description: e instanceof ApiError ? e.message : '请稍后重试' }),
  })

  // 同一个人可能有多个会话（多设备），按用户聚合展示更符合运营的心智
  const byUser = (data?.data ?? []).reduce<Record<string, SessionRow[]>>((acc, s) => {
    ;(acc[s.userId] ??= []).push(s)
    return acc
  }, {})

  return (
    <Page>
      <PageHeader
        title="在线会话"
        description="当前有效的登录会话。停用账号会自动吊销其全部会话，这里也可以单独强制下线。"
      />

      <Toolbar trailing={<Count value={data?.data.length} unit="个会话" />}>
        <span className="text-[12.5px] text-mut2">每 30 秒自动刷新</span>
      </Toolbar>

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow className="border-line/50 hover:bg-transparent">
              <TableHead className="section-label pl-6">账号</TableHead>
              <TableHead className="section-label">角色</TableHead>
              <TableHead className="section-label">设备</TableHead>
              <TableHead className="section-label">IP</TableHead>
              <TableHead className="section-label">登录时间</TableHead>
              <TableHead className="section-label">过期时间</TableHead>
              <TableHead className="section-label pr-6 text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 4 }, (_, i) => (
                <TableRow key={i} className="border-line/40">
                  <TableCell colSpan={7} className="px-6 py-2.5">
                    <Skeleton className="h-7 w-full rounded-apple-sm" />
                  </TableCell>
                </TableRow>
              ))}

            {data?.data.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7}>
                  <EmptyState icon={MonitorSmartphone} title="当前没有活跃会话" />
                </TableCell>
              </TableRow>
            )}

            {data?.data.map((s) => {
              const isSelf = s.userId === me?.user.id
              const sameUserCount = byUser[s.userId]?.length ?? 1

              return (
                <TableRow key={s.id} className="row-hover border-line/40">
                  <TableCell className="pl-6">
                    <div className="flex items-center gap-2">
                      <span className="text-[13.5px] text-txt">{s.name}</span>
                      {isSelf && (
                        <Badge className="h-4 bg-cyan/15 px-1.5 text-[10px] text-cyan hover:bg-cyan/15">
                          当前
                        </Badge>
                      )}
                      {sameUserCount > 1 && (
                        <span className="font-mono text-[10.5px] text-mut2">
                          {sameUserCount} 个会话
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[11px] text-mut2">{s.username}</div>
                  </TableCell>

                  <TableCell>
                    <span className="text-[12px] text-mut">{roleLabel(s.role)}</span>
                  </TableCell>

                  <TableCell className="max-w-[220px]">
                    <span className="block truncate text-[11.5px] text-mut" title={s.userAgent ?? ''}>
                      {describeUserAgent(s.userAgent)}
                    </span>
                  </TableCell>

                  <TableCell className="tnum font-mono text-[11px] text-mut2">
                    {s.ipAddress || '—'}
                  </TableCell>
                  <TableCell className="tnum font-mono text-[11px] text-mut2">
                    {formatDateTime(s.createdAt)}
                  </TableCell>
                  <TableCell className="tnum font-mono text-[11px] text-mut2">
                    {formatDateTime(s.expiresAt)}
                  </TableCell>

                  <TableCell className="pr-6 text-right">
                    {can('user', 'revokeSession') && !isSelf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="pressable h-8 rounded-apple-sm text-[12.5px] text-rose hover:bg-rose/10 hover:text-rose"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(s.userId)}
                      >
                        强制下线
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableCard>
    </Page>
  )
}

/** 把 UA 串压成一句人话。识别不出来就原样截断，不猜。 */
function describeUserAgent(ua: string | null): string {
  if (!ua) return '未知设备'

  const os = /Mac OS X/.test(ua)
    ? 'macOS'
    : /Windows/.test(ua)
      ? 'Windows'
      : /Android/.test(ua)
        ? 'Android'
        : /iPhone|iPad/.test(ua)
          ? 'iOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : null

  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Safari\//.test(ua)
        ? 'Safari'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : null

  if (os && browser) return `${browser} · ${os}`
  return ua.slice(0, 40)
}
